import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import WorkCard from "@/components/base/WorkCard";
import FilterSidebar from "@/pages/category/components/FilterSidebar";
import { allCategories, getCategory, sortOptions, worksByCategory, sortWorks } from "@/lib/content";
import type { SortKey } from "@/lib/content";

const PAGE_SIZE = 12;

export default function Category() {
  const { type = "all" } = useParams();
  const [params, setParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const keyword = params.get("keyword") ?? "";
  const region = params.get("region") ?? "";
  const year = params.get("year") ?? "";
  const sortValue = params.get("sort");
  const sort: SortKey = sortValue === "hot" || sortValue === "rating" ? sortValue : "latest";
  const pageParam = Number(params.get("page") ?? "1");

  const category = getCategory(type);
  const categoryName = type === "all" ? "全部作品" : category?.name ?? "全部作品";

  useEffect(() => {
    document.title = keyword
      ? `搜索「${keyword}」 - 亚洲无码高清成人影视`
      : `${categoryName} - 亚洲无码高清成人影视`;
    const meta = document.querySelector('meta[name="description"]');
    const content = keyword
      ? `亚洲无码网站全站搜索结果：「${keyword}」相关的高清成人影视作品资料、剧集目录与相关推荐。`
      : `浏览亚洲无码网站${categoryName}分类高清成人影视资源，可按地区、年份筛选并按热度排序。`;
    if (meta) meta.setAttribute("content", content);
  }, [categoryName, keyword]);

  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value) next.delete(key);
      else next.set(key, value);
    });
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };

  const resetAll = () => {
    setParams(new URLSearchParams());
    setFilterOpen(false);
  };

  const filtered = useMemo(() => {
    const base = worksByCategory(type);
    const byKeyword = keyword
      ? base.filter((item) =>
          `${item.title} ${item.region} ${item.director} ${item.tags.join(" ")}`
            .toLowerCase()
            .includes(keyword.trim().toLowerCase()),
        )
      : base;
    const byRegion = region ? byKeyword.filter((item) => item.region === region) : byKeyword;
    const byYear = year ? byRegion.filter((item) => String(item.year) === year) : byRegion;
    return sortWorks(byYear, sort);
  }, [type, keyword, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, Number.isFinite(pageParam) ? pageParam : 1), totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const filterProps = {
    type,
    region,
    year,
    sort,
    onChange: update,
    onReset: resetAll,
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-300/50 bg-background-100">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8 md:py-8">
          <nav className="flex flex-wrap items-center gap-1.5 text-[11px] text-foreground-600">
            <Link to="/" className="cursor-pointer whitespace-nowrap transition hover:text-primary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-700" />
            <Link to="/category/all" className="cursor-pointer whitespace-nowrap transition hover:text-primary-300">
              作品分类
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-700" />
            <span className="whitespace-nowrap text-foreground-300">{keyword ? "搜索结果" : categoryName}</span>
          </nav>

          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div className="min-w-0">
              <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
                {keyword ? `搜索：${keyword}` : categoryName}
              </h1>
              <p className="mt-2 max-w-2xl text-[12px] leading-relaxed text-foreground-500 md:text-[13px]">
                {keyword
                  ? `在${categoryName}范围内找到 ${filtered.length} 部与「${keyword}」相关的作品，可继续按地区与年份筛选。`
                  : category?.desc ?? "全站 50 部高清成人影视作品资料，可按题材、地区与年份自由组合筛选。"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md border border-background-300/60 px-2.5 py-1.5 text-[11px] text-foreground-500">
                共 <strong className="text-primary-400">{filtered.length}</strong> 部
              </span>
              {keyword ? (
                <button
                  type="button"
                  onClick={() => update({ keyword: undefined })}
                  className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-accent-600/50 px-2.5 py-1.5 text-[11px] text-accent-300 transition hover:bg-accent-950/60"
                >
                  清除搜索词
                  <i className="ri-close-line" />
                </button>
              ) : null}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-1.5">
            <Link
              to="/category/all"
              className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-[12px] transition ${
                type === "all"
                  ? "border-primary-500 bg-primary-500 font-medium text-background-950"
                  : "border-background-300/70 text-foreground-400 hover:border-primary-700/60 hover:text-primary-300"
              }`}
            >
              全部
            </Link>
            {allCategories.map((item) => (
              <Link
                key={item.key}
                to={`/category/${item.key}`}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-[12px] transition ${
                  type === item.key
                    ? "border-primary-500 bg-primary-500 font-medium text-background-950"
                    : "border-background-300/70 text-foreground-400 hover:border-primary-700/60 hover:text-primary-300"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-6 px-4 py-8 md:px-8 md:py-10 lg:flex-row lg:gap-8">
        <aside className="hidden w-[248px] flex-shrink-0 lg:block">
          <div className="sticky top-[88px] rounded-lg border border-background-300/50 bg-background-100 p-4">
            <FilterSidebar {...filterProps} />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/70 px-3 py-2 text-[12px] text-foreground-200 transition hover:border-primary-600/70 hover:text-primary-300 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选
                {(region || year || keyword) && (
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-bold text-background-950">
                    {[region, year, keyword].filter(Boolean).length}
                  </span>
                )}
              </button>
              <div className="hidden items-center gap-1 lg:flex">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => update({ sort: option.key })}
                    className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-1.5 text-[12px] transition ${
                      sort === option.key
                        ? "bg-background-200 text-primary-300"
                        : "text-foreground-500 hover:text-foreground-200"
                    }`}
                  >
                    <i className={option.icon} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-foreground-600">
              第 {currentPage} / {totalPages} 页 · 每页 {PAGE_SIZE} 部
            </p>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
                {pageItems.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => update({ page: String(currentPage - 1) })}
                  className={`flex h-9 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border px-3 text-[12px] transition ${
                    currentPage <= 1
                      ? "cursor-not-allowed border-background-300/40 text-foreground-700"
                      : "border-background-300/70 text-foreground-300 hover:border-primary-600/70 hover:text-primary-300"
                  }`}
                >
                  <i className="ri-arrow-left-s-line" />
                  上一页
                </button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => update({ page: String(pageNumber) })}
                    className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border text-[12px] transition ${
                      pageNumber === currentPage
                        ? "border-primary-500 bg-primary-500 font-semibold text-background-950"
                        : "border-background-300/70 text-foreground-400 hover:border-primary-600/70 hover:text-primary-300"
                    }`}
                  >
                    {pageNumber}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => update({ page: String(currentPage + 1) })}
                  className={`flex h-9 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border px-3 text-[12px] transition ${
                    currentPage >= totalPages
                      ? "cursor-not-allowed border-background-300/40 text-foreground-700"
                      : "border-background-300/70 text-foreground-300 hover:border-primary-600/70 hover:text-primary-300"
                  }`}
                >
                  下一页
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-primary-500">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-100">没有符合条件的作品</h2>
              <p className="mt-2 max-w-md text-[13px] leading-relaxed text-foreground-500">
                当前筛选组合下暂无收录作品。可以尝试放宽地区或年份条件，或直接重置所有筛选重新浏览。
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={resetAll}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-[13px] font-semibold text-background-950 transition hover:bg-primary-400"
                >
                  <i className="ri-restart-line" />
                  重置筛选条件
                </button>
                <Link
                  to="/category/all"
                  className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2.5 text-[13px] text-foreground-300 transition hover:border-primary-600/70 hover:text-primary-300"
                >
                  浏览全部作品
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {filterOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setFilterOpen(false)}
            className="absolute inset-0 cursor-pointer bg-background-950/70"
          />
          <div className="fade-in absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-xl border-t border-background-300/60 bg-background-100 px-4 pb-6 pt-4">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-heading text-sm font-semibold text-foreground-100">筛选与排序</span>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                aria-label="关闭筛选"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300/70 text-foreground-300"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterSidebar {...filterProps} />
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-5 flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-950"
            >
              查看 {filtered.length} 部作品
            </button>
          </div>
        </div>
      ) : null}
    </SiteLayout>
  );
}